import { Injectable, Optional } from '@angular/core';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import {
    OrgDraft,
    OrgDraftPart,
    OrgDraftStep,
    StaffRole,
    StaffRow,
    ORG_DRAFT_MAX_PARTS,
    ORG_DRAFT_MAX_STAFF,
} from '@newgenus/common';

/**
 * Plan §16.3 — wizard-side structural limits.
 *
 * `ORG_DRAFT_MAX_PARTS` is the **default** wizard cap. Admin can override at
 * runtime by writing `systemConfig/orgWizardLimits` in Firestore:
 *   `{ maxParts: 1000, maxDepth: 8 }`
 * The service reads that doc once on construction and exposes the resolved
 * value via the `maxParts` / `maxDepth` getters. Falls back to these
 * constants if the doc is missing or unreadable.
 *
 * NOTE: the backend `bulkAddParts` task processor still chunks at 50 parts
 * per command doc (Firestore 500-write batch budget). Drafts may be built
 * up to `maxParts`, but submitting an org with >50 parts requires the
 * chunked-import work tracked as P3 in the org-creation plan.
 */
// `ORG_DRAFT_MAX_PARTS` (1000) and `ORG_DRAFT_MAX_STAFF` (200) are owned by
// @newgenus/common — re-exported here (not re-declared) so the wizard shares
// ONE source of truth. The backend keeps a runtime mirror it can't avoid
// (Functions can't import @newgenus/common at runtime — esbuild bundle:false);
// a drift-guard test ties that mirror to these values. See
// apps/newgenus-backend/src/orgCreation/wizardLimits.ts.
export { ORG_DRAFT_MAX_PARTS, ORG_DRAFT_MAX_STAFF };
/** Wizard-side only (no backend equivalent). */
export const ORG_DRAFT_MAX_DEPTH = 8;

/** Hierarchical ordering of part types — index === maximum allowed depth offset from root. */
const PART_TYPE_ORDER: ReadonlyArray<OrgDraftPart['partType']> = [
    'executive',
    'division',
    'department',
    'section',
    'unit',
    'post',
];

/**
 * Maximum number of undo entries kept in the in-memory ring buffer (D7).
 */
export const UNDO_RING_BUFFER_SIZE = 50;

/**
 * Maximum number of undo/redo entries persisted to Firestore alongside the
 * draft. Smaller than the in-memory buffer to keep the doc well under
 * Firestore's 1 MB hard limit (each entry contains a full prior OrgDraft +
 * a parallel BuilderTreeNode[] snapshot for Step 3).
 */
export const PERSISTED_HISTORY_LIMIT = 20;

/**
 * Patch applied to the wizard state. `null` means "remove this top-level field".
 * Two-level deep merge (top-level keys + their direct children) is sufficient
 * for the shape of `OrgDraft` in P1.
 */
export type WizardPatch = Partial<OrgDraft>;

/**
 * In-memory state container for the org-creation wizard.
 *
 * - Holds the live `OrgDraft` snapshot the user is editing.
 * - Provides `dispatch` / `undo` / `redo` for granular edits.
 * - Emits `change$` events the host wizard component listens to in order to
 *   trigger autosave (debounced — see {@link OrgCreationWizardComponent}).
 *
 * Plan reference: §8 — "Undo / redo".
 *
 * P1 scope: the patch model is deliberately simple — the wizard never needs
 * to undo a single character of typing inside a field; undo is meaningful at
 * the step level (a structural change, a staff row added, a feature toggled).
 * `immer` is not required at this granularity.
 */
/**
 * Opaque per-step UI snapshot that rides alongside the OrgDraft in undo/redo
 * history. Used for transient view-only state that the persisted draft can't
 * carry — e.g. Step 3's BuilderTreeNode[] (per-node `dirty`/`moved`/`isNew`/
 * `_original` markers + colour swatches that only exist inside the in-memory
 * builder, not in the flattened OrgDraftPart[]).
 *
 * Lives entirely in this service. Never written to Firestore.
 */
export type WizardUiSnapshot = Record<string, unknown>;

/**
 * One undo/redo entry. Stores the *before* and *after* values of just the
 * top-level draft keys touched by the dispatch — not the whole draft — plus
 * the step the edit was made on so we can keep per-step undo histories
 * isolated. Undoing on Step 3 must never roll back a Step 2 basics edit.
 */
export interface WizardHistoryEntry {
    step: OrgDraftStep;
    /** Prior values of every top-level key the patch touched (whole-field copies). */
    before: WizardPatch;
    /** New values applied by the patch (whole-field copies of the resulting fields). */
    after: WizardPatch;
    /** UI snapshot before the edit — only restored on undo when the entry’s step owns UI state (Step 3). */
    uiBefore: WizardUiSnapshot | null;
    /** UI snapshot after the edit — only restored on redo when the entry’s step owns UI state (Step 3). */
    uiAfter: WizardUiSnapshot | null;
}

/**
 * Serialisable shape of the undo/redo history. Lives in `OrgDraft.history`
 * so undo/redo survives page refreshes. Stored as a per-step map so each
 * step’s history is isolated from every other step’s.
 */
export interface WizardHistorySnapshot {
    perStep: Partial<Record<OrgDraftStep, { undo: WizardHistoryEntry[]; redo: WizardHistoryEntry[] }>>;
}

@Injectable()
export class OrgWizardStateService {
    private state$$ = new BehaviorSubject<OrgDraft | null>(null);
    private change$$ = new Subject<{
        draft: OrgDraft;
        source: 'load' | 'dispatch' | 'undo' | 'redo' | 'navigate';
        ui: WizardUiSnapshot | null;
    }>();

    /** Resolved structural caps — start at the constant defaults, overridden
     *  by `systemConfig/orgWizardLimits` if that doc exists. */
    private _maxParts = ORG_DRAFT_MAX_PARTS;
    private _maxDepth = ORG_DRAFT_MAX_DEPTH;

    /** Effective wizard cap on total parts. Reads from
     *  `systemConfig/orgWizardLimits.maxParts` when present. */
    public get maxParts(): number {
        return this._maxParts;
    }
    /** Effective wizard cap on hierarchy depth. */
    public get maxDepth(): number {
        return this._maxDepth;
    }

    constructor(@Optional() afs?: AngularFirestore) {
        // AngularFirestore is optional so unit tests / non-DI consumers don't
        // need to provide it. When present we fetch the override doc once and
        // adopt any sane values it contains. Failures are silent — the
        // defaults above remain in effect.
        if (!afs) return;
        try {
            afs.doc<{ maxParts?: number; maxDepth?: number }>('systemConfig/orgWizardLimits')
                .valueChanges()
                .pipe(take(1))
                .subscribe({
                    next: (cfg) => {
                        if (cfg?.maxParts && cfg.maxParts > 0) this._maxParts = cfg.maxParts;
                        if (cfg?.maxDepth && cfg.maxDepth > 0) this._maxDepth = cfg.maxDepth;
                    },
                    error: () => {
                        /* keep defaults */
                    },
                });
        } catch {
            /* keep defaults */
        }
    }

    /**
     * Per-step undo/redo stacks. Each step gets its own pair so undoing on
     * one step never reaches into another’s history. Step 3 also rides its
     * own UI snapshot through these entries (BuilderTreeNode markers).
     */
    private historyByStep: Map<OrgDraftStep, { undo: WizardHistoryEntry[]; redo: WizardHistoryEntry[] }> = new Map();
    private currentUi: WizardUiSnapshot | null = null;

    public readonly state$: Observable<OrgDraft | null> = this.state$$.asObservable();
    /** Fires whenever the state changes — used by the wizard shell to drive autosave. */
    public readonly change$ = this.change$$.asObservable();

    public get snapshot(): OrgDraft | null {
        return this.state$$.value;
    }

    /**
     * Replace the entire state — used when a draft loads from Firestore or
     * when the wizard switches between drafts.
     */
    public load(draft: OrgDraft): void {
        this.historyByStep.clear();
        // Seed the transient UI snapshot from the persisted draft so per-step
        // view-only state (Step 3's BuilderTreeNode[] in particular) survives
        // a page refresh. Cloned defensively so wizard mutations don't write
        // back through the OrgDraft object reference.
        this.currentUi = this.cloneUi((draft.uiSnapshot as WizardUiSnapshot | undefined) ?? null);
        // Re-hydrate undo/redo so the top-bar arrows continue exactly where
        // the user left off before refreshing.
        this.restoreHistory((draft.history as unknown as WizardHistorySnapshot | undefined) ?? null);
        this.state$$.next(draft);
        this.change$$.next({ draft, source: 'load', ui: this.currentUi });
    }

    public clear(): void {
        this.historyByStep.clear();
        this.currentUi = null;
        this.state$$.next(null);
    }

    /** The transient UI snapshot riding alongside the current draft. */
    public get uiSnapshot(): WizardUiSnapshot | null {
        return this.currentUi;
    }

    /**
     * Apply a top-level patch to the current draft. Pushes the previous state
     * onto the undo stack and clears the redo stack (standard editor semantics).
     *
     * `pushHistory: false` is for tiny edits (e.g. an internal flag flip) that
     * should not pollute the undo stack.
     */
    public dispatch(
        patch: WizardPatch,
        opts: { pushHistory?: boolean; ui?: WizardUiSnapshot | null; source?: 'dispatch' | 'navigate' } = {}
    ): void {
        const current = this.state$$.value;
        if (!current) return;

        const pushHistory = opts.pushHistory !== false;
        const step = current.currentStep;

        // Build the resulting draft up-front so we can capture both `before`
        // (full prior values of every touched key) and `after` (full post
        // values of those same keys, after the basics two-level merge).
        const next: OrgDraft = { ...current, ...patch };
        if (patch.basics) {
            next.basics = {
                ...current.basics,
                ...patch.basics,
                settings: { ...current.basics.settings, ...(patch.basics.settings ?? {}) },
            };
        }

        if (pushHistory) {
            const before: WizardPatch = {};
            const after: WizardPatch = {};
            for (const key of Object.keys(patch) as Array<keyof OrgDraft>) {
                // Skip pure step-pointer changes — those go through `setStep`
                // with pushHistory:false anyway, but guard here too.
                if (key === 'history' || key === 'uiSnapshot') continue;
                (before as Record<string, unknown>)[key] = this.cloneFieldValue(current[key]);
                (after as Record<string, unknown>)[key] = this.cloneFieldValue(next[key]);
            }
            const stepHistory = this.getStepHistory(step);
            const uiBefore = this.cloneUi(this.currentUi);
            stepHistory.undo.push({ step, before, after, uiBefore, uiAfter: null });
            if (stepHistory.undo.length > UNDO_RING_BUFFER_SIZE) stepHistory.undo.shift();
            stepHistory.redo = [];
        }

        // Update the transient UI snapshot only when the caller supplies one.
        // `ui: null` explicitly clears it; `ui` omitted leaves the current ui in place.
        if ('ui' in opts) {
            this.currentUi = this.cloneUi(opts.ui ?? null);
        }

        if (pushHistory) {
            const stepHistory = this.getStepHistory(step);
            const lastEntry = stepHistory.undo[stepHistory.undo.length - 1];
            if (lastEntry) lastEntry.uiAfter = this.cloneUi(this.currentUi);
        }

        this.state$$.next(next);
        this.change$$.next({ draft: next, source: opts.source ?? 'dispatch', ui: this.currentUi });
    }

    public undo(): void {
        const current = this.state$$.value;
        if (!current) return;
        const stepHistory = this.getStepHistory(current.currentStep);
        const entry = stepHistory.undo.pop();
        if (!entry) return;

        const next: OrgDraft = { ...current, ...(entry.before as Partial<OrgDraft>) };
        // UI snapshot is owned by Step 3 only — restoring `uiBefore` for any
        // other step would clobber the live builder markers belonging to a
        // different step's session.
        if (entry.step === 3) {
            this.currentUi = this.cloneUi(entry.uiBefore);
        }
        stepHistory.redo.push(entry);
        this.state$$.next(next);
        this.change$$.next({ draft: next, source: 'undo', ui: this.currentUi });
    }

    public redo(): void {
        const current = this.state$$.value;
        if (!current) return;
        const stepHistory = this.getStepHistory(current.currentStep);
        const entry = stepHistory.redo.pop();
        if (!entry) return;

        const next: OrgDraft = { ...current, ...(entry.after as Partial<OrgDraft>) };
        if (entry.step === 3) {
            this.currentUi = this.cloneUi(entry.uiAfter);
        }
        stepHistory.undo.push(entry);
        this.state$$.next(next);
        this.change$$.next({ draft: next, source: 'redo', ui: this.currentUi });
    }

    public canUndo(): boolean {
        const current = this.state$$.value;
        if (!current) return false;
        return (this.historyByStep.get(current.currentStep)?.undo.length ?? 0) > 0;
    }

    public canRedo(): boolean {
        const current = this.state$$.value;
        if (!current) return false;
        return (this.historyByStep.get(current.currentStep)?.redo.length ?? 0) > 0;
    }

    private getStepHistory(step: OrgDraftStep): { undo: WizardHistoryEntry[]; redo: WizardHistoryEntry[] } {
        let h = this.historyByStep.get(step);
        if (!h) {
            h = { undo: [], redo: [] };
            this.historyByStep.set(step, h);
        }
        return h;
    }

    // ─── History serialisation (for autosave round-trip across refreshes) ───
    /**
     * Serialisable snapshot of the per-step undo/redo history. The wizard's
     * autosave persists this alongside the draft so undo/redo survives a
     * page refresh. Each step's stack is independently capped at the most
     * recent {@link PERSISTED_HISTORY_LIMIT} entries to keep the persisted
     * document well under Firestore's 1 MB limit.
     */
    public exportHistory(): WizardHistorySnapshot {
        const perStep: WizardHistorySnapshot['perStep'] = {};
        for (const [step, h] of this.historyByStep.entries()) {
            const undo = h.undo.slice(-PERSISTED_HISTORY_LIMIT).map((e) => this.cloneEntry(e));
            const redo = h.redo.slice(-PERSISTED_HISTORY_LIMIT).map((e) => this.cloneEntry(e));
            if (undo.length || redo.length) perStep[step] = { undo, redo };
        }
        return { perStep };
    }

    /**
     * Restore the per-step undo/redo stacks from a persisted snapshot. Called
     * by the wizard shell right after {@link load} so undo/redo continues
     * exactly where the user left off before the refresh. Silently ignores
     * malformed input — including the legacy whole-draft snapshot shape from
     * earlier builds, which is incompatible and gets dropped.
     */
    public restoreHistory(snapshot: WizardHistorySnapshot | null | undefined): void {
        this.historyByStep.clear();
        if (!snapshot || typeof snapshot !== 'object') return;
        const perStep = (snapshot as WizardHistorySnapshot).perStep;
        if (!perStep || typeof perStep !== 'object') return; // legacy shape — discard
        for (const [stepKey, stacks] of Object.entries(perStep)) {
            const step = Number(stepKey) as OrgDraftStep;
            if (!step || step < 1 || step > 6) continue;
            const undo = Array.isArray(stacks?.undo) ? stacks!.undo : [];
            const redo = Array.isArray(stacks?.redo) ? stacks!.redo : [];
            this.historyByStep.set(step, {
                undo: undo.slice(-UNDO_RING_BUFFER_SIZE).map((e) => this.cloneEntry(e)),
                redo: redo.slice(-UNDO_RING_BUFFER_SIZE).map((e) => this.cloneEntry(e)),
            });
        }
    }

    /**
     * Convenience wrapper: change only the current step pointer, no history
     * push. Tagged with `source: 'navigate'` so the wizard's autosave
     * pipeline can ignore pure navigation — the next real edit will carry the
     * updated `currentStep` along with it, so refresh / draft-picker still
     * see an accurate step pointer without us doing a Firestore write per
     * Next/Back click.
     */
    public setStep(step: OrgDraftStep): void {
        this.dispatch({ currentStep: step }, { pushHistory: false, source: 'navigate' });
    }

    // ─── Structure editing helpers (Step 3) ──────────────────────────────────
    // All helpers dispatch a new `structure` array (immutable update) so subscribers
    // re-render naturally. Each push goes onto the undo stack.

    public addPart(parentKey: string, name: string, partType: OrgDraftPart['partType']): void {
        const current = this.snapshot;
        if (!current) return;
        if (current.structure.length >= this._maxParts) return;

        const parent = parentKey === '' ? null : (current.structure.find((p) => p.partKey === parentKey) ?? null);
        const depth = parent ? parent.depth + 1 : 0;
        if (depth >= this._maxDepth) return;

        // Next sibling id is the max+1 within the parent.
        const siblings = current.structure.filter((p) => p.parentPartKey === parentKey);
        const nextId = siblings.length ? Math.max(...siblings.map((s) => s.id)) + 1 : 0;

        const newPart: OrgDraftPart = {
            partKey: this.makePartKey(),
            name: name.trim() || 'New part',
            partType,
            parentPartKey: parentKey,
            id: nextId,
            depth,
        };
        this.dispatch({ structure: [...current.structure, newPart] });
    }

    public renamePart(partKey: string, name: string): void {
        const current = this.snapshot;
        if (!current) return;
        const trimmed = name.trim();
        if (!trimmed) return;
        const next = current.structure.map((p) => (p.partKey === partKey ? { ...p, name: trimmed } : p));
        this.dispatch({ structure: next });
    }

    public removePart(partKey: string): void {
        const current = this.snapshot;
        if (!current) return;
        // Collect the subtree (the part and every descendant).
        const toRemove = new Set<string>([partKey]);
        let changed = true;
        while (changed) {
            changed = false;
            for (const p of current.structure) {
                if (!toRemove.has(p.partKey) && toRemove.has(p.parentPartKey)) {
                    toRemove.add(p.partKey);
                    changed = true;
                }
            }
        }
        const next = current.structure.filter((p) => !toRemove.has(p.partKey));
        this.dispatch({ structure: next });
    }

    public setPartType(partKey: string, partType: OrgDraftPart['partType']): void {
        const current = this.snapshot;
        if (!current) return;
        const next = current.structure.map((p) => (p.partKey === partKey ? { ...p, partType } : p));
        this.dispatch({ structure: next });
    }

    public setHeaderRole(partKey: string, headerRole: OrgDraftPart['headerRole'] | null): void {
        const current = this.snapshot;
        if (!current) return;
        const next = current.structure.map((p) => {
            if (p.partKey !== partKey) return p;
            const { headerRole: _drop, ...rest } = p;
            return headerRole ? { ...rest, headerRole } : (rest as OrgDraftPart);
        });
        this.dispatch({ structure: next });
    }

    /**
     * Move a part (and its subtree) to a new parent at a new index among the new siblings.
     * `newIndex` is the position in the parent's sibling list (0-based) — pass
     * `Number.POSITIVE_INFINITY` to append.
     */
    public movePart(partKey: string, newParentKey: string, newIndex: number): void {
        const current = this.snapshot;
        if (!current) return;
        if (partKey === newParentKey) return;

        // Disallow reparenting under one of your own descendants — would create a cycle.
        const descendants = this.collectDescendants(current.structure, partKey);
        if (descendants.has(newParentKey)) return;

        const moving = current.structure.find((p) => p.partKey === partKey);
        if (!moving) return;

        const newParent = newParentKey === '' ? null : current.structure.find((p) => p.partKey === newParentKey);
        if (newParentKey !== '' && !newParent) return;
        const newDepth = newParent ? newParent.depth + 1 : 0;

        // Bail if moving subtree would exceed the depth cap.
        const subtreeMaxDepth = this.subtreeMaxDepthDelta(current.structure, partKey);
        if (newDepth + subtreeMaxDepth >= ORG_DRAFT_MAX_DEPTH) return;

        const newSiblings = current.structure
            .filter((p) => p.parentPartKey === newParentKey && p.partKey !== partKey)
            .sort((a, b) => a.id - b.id);

        const insertAt = Math.max(0, Math.min(newIndex, newSiblings.length));
        const reorderedSiblings: OrgDraftPart[] = [...newSiblings];
        reorderedSiblings.splice(insertAt, 0, { ...moving, parentPartKey: newParentKey, depth: newDepth });

        const next = current.structure.map((p) => {
            // Renumber every node in the new sibling group, and update the moved node itself.
            const sibIdx = reorderedSiblings.findIndex((s) => s.partKey === p.partKey);
            if (sibIdx !== -1) {
                const sib = reorderedSiblings[sibIdx];
                return { ...p, parentPartKey: sib.parentPartKey, depth: sib.depth, id: sibIdx };
            }
            // Subtree members: recompute depth relative to moved node.
            if (descendants.has(p.partKey)) {
                const depthDelta = newDepth - moving.depth;
                return { ...p, depth: p.depth + depthDelta };
            }
            return p;
        });

        this.dispatch({ structure: next });
    }

    private collectDescendants(structure: OrgDraftPart[], rootKey: string): Set<string> {
        const out = new Set<string>([rootKey]);
        let changed = true;
        while (changed) {
            changed = false;
            for (const p of structure) {
                if (!out.has(p.partKey) && out.has(p.parentPartKey)) {
                    out.add(p.partKey);
                    changed = true;
                }
            }
        }
        return out;
    }

    private subtreeMaxDepthDelta(structure: OrgDraftPart[], rootKey: string): number {
        const root = structure.find((p) => p.partKey === rootKey);
        if (!root) return 0;
        const descendants = this.collectDescendants(structure, rootKey);
        let max = 0;
        for (const key of descendants) {
            const p = structure.find((x) => x.partKey === key);
            if (p) max = Math.max(max, p.depth - root.depth);
        }
        return max;
    }

    private makePartKey(): string {
        // Match the format used by the scaffold seeds in step-1-source.component.ts.
        return `part-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }

    /**
     * Replace the entire `structure` array in one shot. Used by Step 3's
     * shared `OrgPartTreeBuilderComponent` wrapper (R9.2) — the builder
     * mutates an in-memory tree, then on save flattens it to `OrgDraftPart[]`
     * and hands it back to the wizard via this method.
     *
     * Performs a single dispatch so the change ends up as one undo entry,
     * not one per part.
     */
    public setStructure(
        parts: OrgDraftPart[],
        opts: { pushHistory?: boolean; ui?: WizardUiSnapshot | null } = {}
    ): void {
        const current = this.snapshot;
        if (!current) return;
        // Defensive clone so the caller can keep mutating their copy.
        const next = parts.map((p) => ({ ...p }));
        this.dispatch({ structure: next }, opts);
    }

    // ─── CEO post (crown 👑) — addendum 19 ───────────────────────────────────
    /**
     * Returns the partKey of the post currently flagged `isCeoPost === true`,
     * or `null` if no post carries the flag (or the structure has no posts).
     */
    public get ceoPostKey(): string | null {
        const draft = this.snapshot;
        if (!draft) return null;
        const explicit = draft.structure.find(
            (p) => p.partType === 'post' && (p as { isCeoPost?: boolean }).isCeoPost === true
        );
        return explicit?.partKey ?? null;
    }

    /**
     * Set (or move) the CEO crown to `partKey`. Singleton invariant: any
     * other part already carrying `isCeoPost: true` is cleared in the same
     * dispatch so undo restores the prior crown atomically.
     *
     * No-op when `partKey` is empty, doesn't refer to an existing part, or
     * refers to a non-`post` part.
     */
    public setCeoPost(partKey: string, opts: { pushHistory?: boolean } = {}): void {
        const current = this.snapshot;
        if (!current || !partKey) return;
        const target = current.structure.find((p) => p.partKey === partKey);
        if (!target || target.partType !== 'post') return;
        if (this.ceoPostKey === partKey) return; // no-op, already crowned
        const next = current.structure.map((p) => {
            const cleaned: OrgDraftPart = { ...p };
            if (p.partKey === partKey) {
                (cleaned as { isCeoPost?: boolean }).isCeoPost = true;
            } else if ((p as { isCeoPost?: boolean }).isCeoPost) {
                delete (cleaned as { isCeoPost?: boolean }).isCeoPost;
            }
            return cleaned;
        });
        this.dispatch({ structure: next }, opts);
    }

    /**
     * If the draft has at least one post and none of them carries
     * `isCeoPost: true`, auto-pick the lowest-depth post (ties broken by
     * `id` ASC) — same heuristic the backend orchestrator uses as its
     * safety-net fallback. Pushed without a history entry so it never
     * shows up on the user-visible undo stack.
     *
     * Idempotent — safe to call after every structure change.
     */
    public autoPickCeoPostIfNeeded(): void {
        const current = this.snapshot;
        if (!current) return;
        const posts = current.structure.filter((p) => p.partType === 'post');
        if (posts.length === 0) return;
        const alreadyCrowned = posts.some((p) => (p as { isCeoPost?: boolean }).isCeoPost === true);
        if (alreadyCrowned) return;
        const sorted = posts.slice().sort((a, b) => a.depth - b.depth || a.id - b.id);
        this.setCeoPost(sorted[0].partKey, { pushHistory: false });
    }

    // ─── Features editing helpers (Step 5) ───────────────────────────────────
    /**
     * Replace the whole feature-key selection. Dispatches a new immutable array
     * and (by default) pushes an undo entry — toggling a bundle is a single
     * meaningful unit of work.
     */
    public setFeatures(featureKeys: string[], opts: { pushHistory?: boolean } = {}): void {
        const current = this.snapshot;
        if (!current) return;
        // De-dup + stable order so the autosave debouncer doesn't write
        // re-orderings of the same set as separate revisions.
        const next = Array.from(new Set(featureKeys)).sort();
        // No-op if the selection is unchanged — Step 5 re-seeds the draft
        // every time it mounts so we'd otherwise dispatch (and autosave)
        // every time the user navigates onto the page.
        const prev = current.features ?? [];
        if (prev.length === next.length && prev.every((k, i) => k === next[i])) return;
        this.dispatch({ features: next }, opts);
    }

    public toggleFeature(featureKey: string): void {
        const current = this.snapshot;
        if (!current) return;
        const set = new Set(current.features ?? []);
        if (set.has(featureKey)) set.delete(featureKey);
        else set.add(featureKey);
        this.setFeatures([...set]);
    }

    // ─── Staff editing helpers (Step 4) ──────────────────────────────────────
    // All helpers dispatch a new `staff` array (immutable update). Each push
    // creates an undo entry unless `pushHistory: false` is set — `setStaffResolution`
    // uses no-history because it's the async result of a debounced `checkEmail`
    // and should never end up on the user-visible undo stack.

    public addStaffRow(initial: Partial<StaffRow> = {}): StaffRow | null {
        const current = this.snapshot;
        if (!current) return null;
        const list = current.staff ?? [];
        if (list.length >= ORG_DRAFT_MAX_STAFF) return null;
        const row: StaffRow = {
            rowId: this.makeStaffRowId(),
            email: '',
            resolutionStatus: 'unresolved',
            ...initial,
        };
        this.dispatch({ staff: [...list, row] });
        return row;
    }

    public removeStaffRow(rowId: string): void {
        const current = this.snapshot;
        if (!current) return;
        const list = current.staff ?? [];
        const next = list.filter((r) => r.rowId !== rowId);
        if (next.length === list.length) return;
        this.dispatch({ staff: next });
    }

    public updateStaffRow(rowId: string, patch: Partial<StaffRow>, opts: { pushHistory?: boolean } = {}): void {
        const current = this.snapshot;
        if (!current) return;
        const list = current.staff ?? [];
        let touched = false;
        const next = list.map((r) => {
            if (r.rowId !== rowId) return r;
            touched = true;
            // If the email changed, any prior resolution is stale.
            const emailChanged = patch.email !== undefined && patch.email !== r.email;
            const cleared: Partial<StaffRow> = emailChanged
                ? { resolvedUid: undefined, resolutionStatus: 'unresolved', resolutionCheckedAt: undefined }
                : {};
            return { ...r, ...patch, ...cleared };
        });
        if (!touched) return;
        this.dispatch({ staff: next }, opts);
    }

    public setStaffResolution(
        rowId: string,
        resolution: Pick<StaffRow, 'resolutionStatus' | 'resolvedUid' | 'resolutionCheckedAt'> & {
            firstName?: string;
            lastName?: string;
        }
    ): void {
        const current = this.snapshot;
        if (!current) return;
        const list = current.staff ?? [];
        const next = list.map((r) => (r.rowId === rowId ? { ...r, ...resolution } : r));
        // Resolution lands asynchronously — don't pollute the undo stack with it.
        this.dispatch({ staff: next }, { pushHistory: false });
    }

    public assignStaffToPost(rowId: string, postKey: string | undefined): void {
        // Legacy single-post wrapper — kept so older callers still work, but
        // the multi-post staff stage uses `setStaffPostKeys` directly.
        this.setStaffPostKeys(rowId, postKey ? [postKey] : []);
    }

    /**
     * Replace the full set of posts assigned to a staff row. The wizard table
     * mirrors the New Hat dialog's "Org Posts" pattern: searchable multi-select
     * with selected posts shown as chips. Passing an empty array clears the
     * assignment (row remains in the draft but won't generate an invitation
     * until at least one post is picked — §6.5 gate).
     */
    public setStaffPostKeys(rowId: string, postKeys: string[]): void {
        const unique = Array.from(new Set((postKeys || []).filter(Boolean)));
        this.updateStaffRow(rowId, {
            postKeys: unique,
            // Mirror the first selection into the legacy single field so
            // back-end code that still reads `postKey` (until it's retired)
            // keeps working for drafts created with the new UI.
            postKey: unique[0],
        });
    }

    public setStaffRole(rowId: string, role: StaffRole | undefined): void {
        this.updateStaffRow(rowId, { role });
    }

    /**
     * Bulk-add staff rows from a paste/import (one email per line, optional
     * `Last, First <email>` style is parsed best-effort). Duplicates of
     * already-present emails are skipped silently. Returns the number of rows
     * actually added.
     */
    public bulkAddStaffFromEmails(rawText: string): number {
        const current = this.snapshot;
        if (!current) return 0;
        const list = current.staff ?? [];
        const existingEmails = new Set(list.map((r) => r.email.toLowerCase().trim()).filter(Boolean));
        const remaining = ORG_DRAFT_MAX_STAFF - list.length;
        if (remaining <= 0) return 0;

        const added: StaffRow[] = [];
        const lines = rawText
            .split(/[\r\n,;\t]+/)
            .map((l) => l.trim())
            .filter(Boolean);
        for (const line of lines) {
            if (added.length >= remaining) break;
            // Match `Name <email>` or just `email`.
            const m = line.match(/^(.*?)<\s*([^>\s]+@[^>\s]+)\s*>$/) ?? line.match(/^()(\S+@\S+)$/);
            if (!m) continue;
            const namePart = (m[1] ?? '').trim();
            const email = m[2].toLowerCase().trim();
            if (!email || existingEmails.has(email)) continue;
            existingEmails.add(email);
            const [first = '', ...rest] = namePart.split(/\s+/).filter(Boolean);
            added.push({
                rowId: this.makeStaffRowId(),
                email,
                firstName: first || undefined,
                lastName: rest.length ? rest.join(' ') : undefined,
                resolutionStatus: 'unresolved',
            });
        }
        if (!added.length) return 0;
        this.dispatch({ staff: [...list, ...added] });
        return added.length;
    }

    private makeStaffRowId(): string {
        return `staff-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }

    /**
     * Merge staff rows that share the same email (case-insensitive). Useful
     * for drafts created before the multi-post staff stage existed, where
     * each post assignment lived on its own row. The first occurrence of an
     * email keeps the row identity (and its rowId / resolution state); its
     * postKeys are unioned with every later duplicate's postKeys, and any
     * empty firstName/lastName/role are back-filled from the first
     * non-empty value found across the duplicates. Returns the number of
     * duplicate rows collapsed (0 = nothing to do, no dispatch happens).
     */
    public mergeDuplicateStaffEmails(): number {
        const current = this.snapshot;
        if (!current) return 0;
        const list = current.staff ?? [];
        if (list.length < 2) return 0;

        const byEmail = new Map<string, StaffRow>();
        const order: string[] = [];
        let collapsed = 0;

        for (const row of list) {
            const key = (row.email || '').toLowerCase().trim();
            if (!key) {
                // Empty-email rows can't be deduped — keep them as-is, but
                // give each one a unique synthetic key so they survive.
                const synthetic = `__empty:${row.rowId}`;
                byEmail.set(synthetic, row);
                order.push(synthetic);
                continue;
            }
            const existing = byEmail.get(key);
            if (!existing) {
                byEmail.set(key, {
                    ...row,
                    postKeys: Array.from(new Set((row.postKeys ?? (row.postKey ? [row.postKey] : [])).filter(Boolean))),
                });
                order.push(key);
                continue;
            }
            collapsed += 1;
            const merged = Array.from(
                new Set(
                    [
                        ...(existing.postKeys ?? []),
                        ...((row.postKeys ?? (row.postKey ? [row.postKey] : [])) || []),
                    ].filter(Boolean)
                )
            );
            byEmail.set(key, {
                ...existing,
                postKeys: merged,
                postKey: merged[0],
                firstName: existing.firstName || row.firstName,
                lastName: existing.lastName || row.lastName,
                role: existing.role || row.role,
            });
        }

        if (collapsed === 0) return 0;
        const next = order.map((k) => byEmail.get(k)!).filter(Boolean);
        this.dispatch({ staff: next });
        return collapsed;
    }

    /**
     * True if at least two staff rows share the same (case-insensitive,
     * trimmed) non-empty email. Cheap O(n) check used to gate the
     * "Merge duplicates" toolbar action.
     */
    public hasDuplicateStaffEmails(): boolean {
        const list = this.snapshot?.staff ?? [];
        const seen = new Set<string>();
        for (const r of list) {
            const k = (r.email || '').toLowerCase().trim();
            if (!k) continue;
            if (seen.has(k)) return true;
            seen.add(k);
        }
        return false;
    }

    private cloneUi(ui: WizardUiSnapshot | null): WizardUiSnapshot | null {
        if (!ui) return null;
        // BuilderTreeNode[] (the only consumer today) is plain JSON-ish data
        // — structuredClone handles it. If a future caller stashes something
        // exotic (Date, Map, Set) it'll still clone correctly.
        return structuredClone(ui);
    }

    /**
     * Defensive deep-copy of a single top-level draft field for an undo entry.
     * structuredClone handles arrays / nested objects / Firestore Timestamps;
     * primitives pass through untouched.
     */
    private cloneFieldValue(value: unknown): unknown {
        if (value === null || value === undefined) return value;
        if (typeof value !== 'object') return value;
        return structuredClone(value);
    }

    /** Defensive copy of an undo/redo entry — used when (de)serialising history. */
    private cloneEntry(entry: WizardHistoryEntry): WizardHistoryEntry {
        return {
            step: entry.step,
            before: structuredClone(entry.before ?? {}),
            after: structuredClone(entry.after ?? {}),
            uiBefore: this.cloneUi(entry.uiBefore ?? null),
            uiAfter: this.cloneUi(entry.uiAfter ?? null),
        };
    }
}
